import { API_CONFIG } from '@/config/api';
import { logger } from '@equip/utils/logger';
import { provisioningFlight, setProvisioningFlight } from './provisioningFlight';

const JIT_TIMEOUT_MS = 10_000;

/**
 * Fire-and-forget POST to /api/rest/app/v1/users/me so the DB record reflects
 * any profile or role changes that arrived with the refreshed token.
 *
 * Uses fetch directly to avoid a circular import with httpClient.
 * Shares provisioningFlight with provisionCurrentUser so concurrent calls from
 * either provisioning code path collapse into a single in-flight request.
 */
export const triggerJitProvisioning = (accessToken: string): void => {
  // If a provisioning request is already in-flight (e.g. from the activity
  // heartbeat), skip — the existing request will satisfy this need too.
  if (provisioningFlight) return;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), JIT_TIMEOUT_MS);

  const flight: Promise<void> = fetch(`${API_CONFIG.BASE_URL}/api/rest/app/v1/users/me`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: '{}',
    signal: controller.signal,
  })
    .then((res) => {
      if (!res.ok) {
        logger.warn('JIT provisioning returned non-OK status', {
          component: 'jitProvision',
          status: res.status,
        });
      }
    })
    .catch((err: unknown) => {
      logger.warn(
        'JIT provisioning call failed — role changes may not persist until next refresh',
        {
          component: 'jitProvision',
          error: String(err),
        },
      );
    })
    .finally(() => {
      clearTimeout(timeoutId);
      setProvisioningFlight(null);
    });

  setProvisioningFlight(flight);
};
